const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

/** Serve from memory without network when younger than this. */
const STALE_TIME_MS = 60 * 1000;

/** @type {Map<string, { data: unknown, at: number }>} */
const cache = new Map();
/** @type {Map<string, Promise<unknown>>} */
const inflight = new Map();

export function salesMapCacheKey(tenantId, accountId) {
  const tenant = String(tenantId ?? '').trim().toLowerCase() || 'default';
  const account = String(accountId ?? '').trim();
  return `sales-map::${tenant}::${account}`;
}

export function invalidateSalesMapCache(tenantId, accountId) {
  inflight.delete(salesMapCacheKey(tenantId, accountId));
  cache.delete(salesMapCacheKey(tenantId, accountId));
}

export function invalidateAllSalesMapCache() {
  cache.clear();
  inflight.clear();
}

/** Any cached payload (even past STALE_TIME_MS) for stale-while-revalidate paint. */
export function peekSalesMapCache(tenantId, accountId) {
  if (!tenantId || !accountId) return null;
  return cache.get(salesMapCacheKey(tenantId, accountId))?.data ?? null;
}

export function isSalesMapCacheFresh(tenantId, accountId) {
  if (!tenantId || !accountId) return false;
  const hit = cache.get(salesMapCacheKey(tenantId, accountId));
  return Boolean(hit && Date.now() - hit.at < STALE_TIME_MS);
}

/** Warm cache while user is still on Overview (fire-and-forget). */
export function prefetchAccountSalesMap(tenantId, accountId, userId) {
  if (!tenantId || !accountId) return;
  if (isSalesMapCacheFresh(tenantId, accountId)) return;
  fetchAccountSalesMap(tenantId, accountId, userId, { force: false }).catch(() => {});
}

function salesMapHeaders(tenantId, userId) {
  const headers = { 'Content-Type': 'application/json' };
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

/**
 * GET /api/accounts/:account_id/sales-map?tenant_id=
 * @param {{ force?: boolean, refresh?: boolean, includeNbm?: boolean }} options
 *   force      — bypass FE memory cache / coalesce (normal revalidate)
 *   refresh    — also send refresh=1 so the API recomputes. Refresh button only.
 *   includeNbm — ask the API to compute Next Best Move inline. DEFAULT false.
 *
 * [2026-10-12 perf] includeNbm DEFAULTS TO FALSE and that is the whole fix for
 * the slow Sales Map tab. This call used to hardcode `include_nbm: '1'`, so every
 * cold open paid for the Next Best Move LLM call: measured on prod request logs,
 * p50 0.10s (warm cache) but p95 22.9s and max 29.4s, across three tenants
 * (verypc, terminalfour, constructor_tech) — slow, never failing, so it never
 * showed up as an error.
 *
 * The API has supported the fast path since 2026-07-22 (see salesMapRoutes.ts:
 * "FE's first paint sends include_nbm=0 (scorecard-only, hundreds of ms) then
 * lazy-loads NBM via GET .../sales-map/nbm"). The FE simply never adopted it.
 *
 * Nothing on the Sales Map tab renders NBM: it is read only by
 * handleSuggestNextMove, and NextBestMoveCard is rendered inside NeoChatPanel,
 * returning null without a move. So deferring it costs the user nothing.
 */
export async function fetchAccountSalesMap(tenantId, accountId, userId, options = {}) {
  const { force = false, refresh = false, includeNbm = false } = options;
  if (!tenantId || !accountId) {
    throw new Error('tenant_id and account_id are required');
  }

  const key = salesMapCacheKey(tenantId, accountId);
  const bypassClientCache = force || refresh;

  if (!bypassClientCache) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < STALE_TIME_MS) {
      return hit.data;
    }
    const pending = inflight.get(key);
    if (pending) return pending;
  } else {
    inflight.delete(key);
  }

  const params = new URLSearchParams({
    tenant_id: tenantId,
    include_nbm: includeNbm ? '1' : '0',
  });
  if (userId) params.set('user_id', userId);
  // Server-side cache bust — only for explicit Refresh clicks, never normal loads.
  if (refresh) params.set('refresh', '1');

  const promise = fetch(
    `${API_BASE_URL}/api/accounts/${encodeURIComponent(accountId)}/sales-map?${params.toString()}`,
    {
      credentials: 'include',
      headers: salesMapHeaders(tenantId, userId),
    },
  ).then(async (res) => {
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }
    const data = await res.json();
    cache.set(key, { data, at: Date.now() });
    inflight.delete(key);
    return data;
  }).catch((err) => {
    inflight.delete(key);
    throw err;
  });

  // Don't coalesce unrelated callers onto a long refresh (~3s); they can use BE cache.
  if (!refresh) {
    inflight.set(key, promise);
  }
  return promise;
}

/**
 * GET /api/accounts/:account_id/sales-map/nbm
 *
 * [2026-10-12 perf] The lazy half of the include_nbm=0 fast paint. Serves NBM
 * from the API's full-payload cache when present, otherwise computes it and
 * warms that cache. Called in the background after the tab has painted, so the
 * value is already there by the time the user clicks Suggest next move.
 *
 * Resolves to the NBM object or null. Never throws at the call site's expense —
 * a failure here must not blank a Sales Map that has already rendered.
 */
export async function fetchAccountSalesMapNbm(tenantId, accountId, userId, options = {}) {
  const { refresh = false } = options;
  if (!tenantId || !accountId) return null;

  const params = new URLSearchParams({ tenant_id: tenantId });
  if (userId) params.set('user_id', userId);
  if (refresh) params.set('refresh', '1');

  const res = await fetch(
    `${API_BASE_URL}/api/accounts/${encodeURIComponent(accountId)}/sales-map/nbm?${params.toString()}`,
    {
      credentials: 'include',
      headers: salesMapHeaders(tenantId, userId),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data?.next_best_move ?? null;
}

/**
 * Merge a lazily-fetched NBM into the FE memory cache entry so a tab switch
 * keeps it. No-op when the cached payload has since been replaced.
 */
export function mergeNbmIntoSalesMapCache(tenantId, accountId, nbm) {
  const key = salesMapCacheKey(tenantId, accountId);
  const hit = cache.get(key);
  if (!hit?.data) return;
  cache.set(key, { data: { ...hit.data, next_best_move: nbm }, at: hit.at });
}

/** Rows shown in the main table (excludes footer summary row). */
export function getSalesMapTableRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => r?.sales_point !== 'recommended_next_move');
}

export function getSalesMapRecommendedRow(rows) {
  if (!Array.isArray(rows)) return null;
  return rows.find((r) => r?.sales_point === 'recommended_next_move') ?? null;
}

export function resolveStrengthLabel(row) {
  const label = row?.strength_label;
  const known = ['Missing', 'Very Low', 'Low', 'Medium', 'High', 'Very High'];
  if (label && known.includes(label)) return label;
  return 'Missing';
}

export function isMissingStrengthRow(row) {
  if (row?.strength_label === 'Missing') return true;
  if (row?.strength_label != null) return false;
  return row?.score === 0;
}

export function countMissingSalesMapRows(rows) {
  return getSalesMapTableRows(rows).filter((r) => isMissingStrengthRow(r)).length;
}

/**
 * PATCH /api/accounts/:account_id/sales-map/:sales_point
 */
export async function patchSalesMapOverride(accountId, salesPoint, tenantId, userId, patch) {
  if (!tenantId || !accountId || !salesPoint) {
    throw new Error('tenant_id, account_id, and sales_point are required');
  }

  const r = await fetch(
    `${API_BASE_URL}/api/accounts/${encodeURIComponent(accountId)}/sales-map/${encodeURIComponent(salesPoint)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: salesMapHeaders(tenantId, userId),
      body: JSON.stringify({ tenant_id: tenantId, user_id: userId, ...patch }),
    },
  );

  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(text || `PATCH ${r.status}`);
  }

  const j = await r.json();
  invalidateSalesMapCache(tenantId, accountId);
  return j.row;
}

/**
 * DELETE /api/accounts/:account_id/sales-map/:sales_point/override
 */
export async function deleteSalesMapOverride(accountId, salesPoint, tenantId, userId) {
  if (!tenantId || !accountId || !salesPoint) {
    throw new Error('tenant_id, account_id, and sales_point are required');
  }

  const params = new URLSearchParams({
    tenant_id: tenantId,
    user_id: userId || '',
  });

  const r = await fetch(
    `${API_BASE_URL}/api/accounts/${encodeURIComponent(accountId)}/sales-map/${encodeURIComponent(salesPoint)}/override?${params.toString()}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: salesMapHeaders(tenantId, userId),
    },
  );

  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(text || `DELETE ${r.status}`);
  }

  invalidateSalesMapCache(tenantId, accountId);
}

function parseNextBestMoveValue(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return value.trim() ? { move: value.trim() } : null;
    }
  }
  return typeof value === 'object' ? value : null;
}

/** Strip engine-internal fields before rendering in chat UI. */
export function sanitizeNextBestMoveForDisplay(nbm) {
  if (!nbm || typeof nbm !== 'object') return null;
  const {
    rule_id: _ruleId,
    motion_type: _motionType,
    evidence_basis: _evidenceBasis,
    trigger_sales_points: _triggerSalesPoints,
    _legacy: _legacyFlag,
    ...display
  } = nbm;
  if (!display.move) return null;
  return display;
}

/**
 * Structured NBM from GET sales-map (include_nbm=1).
 * Falls back to legacy recommended_next_move row text when needed.
 */
export function resolveNextBestMove(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const direct =
    parseNextBestMoveValue(payload.next_best_move) ??
    parseNextBestMoveValue(payload.scorecard?.next_best_move);

  if (direct?.move) {
    return {
      ...direct,
      move: String(direct.move).trim(),
    };
  }

  const legacyRow = getSalesMapRecommendedRow(payload.rows);
  if (!legacyRow) return null;

  const move =
    String(legacyRow.recommended_next_step || legacyRow.current_view || '').trim();
  if (!move) return null;

  return {
    move,
    why: legacyRow.gap ? String(legacyRow.gap).trim() : undefined,
    _legacy: true,
  };
}

/** @deprecated V1 — NBM renders in chat only. */
export function isNbmMetadataVisible(payload) {
  return (
    payload?.nbm_metadata_visible === true ||
    payload?.scorecard?.nbm_metadata_visible === true
  );
}
